import type { Asset, StencilMask, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { ABSENT, UNREGISTERED } from '../constants';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { WATERMARK_ASSET_NAME } from 'js/src/libs/consts';

/**
 * Reverse-map a media asset to its MediaRegistry id — the same id `set_media` accepts. Returns
 * ABSENT when the asset isn't a media asset (or has no source data), and UNREGISTERED when it is a
 * media asset with a uuid the registry hasn't seen yet (e.g. a template default). The two are kept
 * distinct so the agent can tell "not a media asset" apart from "a media asset I haven't looked up".
 */
export const getMediaIdFromAsset = (asset: Asset): string => {
    if (!VideoData.isMediaAsset(asset) || !asset.sourceData) {
        return ABSENT;
    }

    const mediaRegistry = MediaRegistry.getInstance();
    const uuid = asset.sourceData.uuid;
    const mediaId = mediaRegistry.getItemIdByUuid(uuid);

    return mediaId || UNREGISTERED;
};

/**
 * The media asset's kind, read from the asset itself (Luminary's `MediaAsset.isVideo`) rather than
 * inferred from a registry lookup: `true` → video, `false` → image, `null` (no source data) →
 * unknown. Keeping this asset-driven means an unregistered video is never mis-reported as an image.
 */
export const getMediaTypeFromAsset = (asset: Asset): 'video' | 'image' | 'unknown' => {
    if (!VideoData.isMediaAsset(asset)) {
        return 'unknown';
    }
    if (asset.isVideo === true) {
        return 'video';
    }
    if (asset.isVideo === false) {
        return 'image';
    }
    return 'unknown';
};

/**
 * Watermarks are ordinary media assets distinguished only by a reserved name — Luminary exposes no
 * watermark type discriminator. Reuses the shared WATERMARK_ASSET_NAME constant (same source of
 * truth as text-contrast-utils) instead of an inline magic string.
 */
export const isWatermarkAsset = (asset: Asset): boolean =>
    VideoData.isMediaAsset(asset) && asset.name === WATERMARK_ASSET_NAME;

const getMediaViewport = (
    asset: TimeAwareMediaAsset,
): { x: number; y: number; width: number; height: number } => {
    if (asset.mask) {
        return (asset.mask as StencilMask).getMaxBounds();
    }
    const { width, height } = asset.root.referenceResolution;
    return { x: 0, y: 0, width, height };
};

const roundToTenth = (value: number): number => Math.round(value * 10) / 10;

/**
 * Media covers its frame rather than letterboxing, so a shape disagreement is paid for entirely in
 * cropped-away pixels — which is why the mismatch ratio doubles as the share of the media lost.
 */
export const getMediaFrameFit = (
    asset: TimeAwareMediaAsset,
): {
    mismatch: number;
    visibleFraction: number;
    mediaAspect: number;
    frameAspect: number;
} | null => {
    const { width: frameWidth, height: frameHeight } = getMediaViewport(asset);
    const { width: mediaWidth, height: mediaHeight } = asset.sourceData ?? {};

    if (!mediaWidth || !mediaHeight || !frameWidth || !frameHeight) {
        return null;
    }

    const mediaAspect = mediaWidth / mediaHeight;
    const frameAspect = frameWidth / frameHeight;
    const mismatch = Math.max(mediaAspect, frameAspect) / Math.min(mediaAspect, frameAspect);

    return { mismatch, visibleFraction: 1 / mismatch, mediaAspect, frameAspect };
};

export const describeAspectRatio = (aspect: number): string => {
    if (aspect >= 2.5) {
        return 'wide banner';
    }
    if (aspect > 1.2) {
        return 'landscape';
    }
    if (aspect >= 0.85) {
        return 'square';
    }
    if (aspect > 0.4) {
        return 'portrait';
    }
    return 'tall strip';
};

export const getVisibleMediaBounds = (asset: TimeAwareMediaAsset): string => {
    const { x, y, width, height } = getMediaViewport(asset);
    return `${roundToTenth(x)} ${roundToTenth(y)} ${roundToTenth(width)} ${roundToTenth(height)}`;
};

export const getFullMediaResolution = (asset: TimeAwareMediaAsset): string => {
    return `${Math.round(asset.width)} ${Math.round(asset.height)}`;
};

/* Get the media translation in pixels. Since the media is always centered, these values are relative to the center of the visible area. */
export const getFullMediaTranslation = (asset: TimeAwareMediaAsset): string => {
    const { width, height } = getMediaViewport(asset);
    // get translation in pixels and remove sign
    let xTranslation = Math.abs(asset.xTranslation * width);
    let yTranslation = Math.abs(asset.yTranslation * height);

    // due to rounding, it's possible for our x or y translation to be out of range.
    // even though this will be absorbed by Luminary, it's confusing to the user so we want to correct it here.
    if (xTranslation > (asset.width - width) / 2.0) {
        xTranslation = (asset.width - width) / 2.0;
    }
    if (yTranslation > (asset.height - height) / 2.0) {
        yTranslation = (asset.height - height) / 2.0;
    }
    // add sign correction
    if (asset.xTranslation < 0) {
        xTranslation *= -1;
    }
    if (asset.yTranslation < 0) {
        yTranslation *= -1;
    }

    // the model always seems to be confused between left/right and up/down even though we provide it as part of the setMedia tool description
    // we're going to re-iterate it here for clarity
    const xDirection =
        asset.xTranslation === 0 ? 'center' : asset.xTranslation < 0 ? 'right' : 'left';
    const yDirection = asset.yTranslation === 0 ? 'center' : asset.yTranslation < 0 ? 'down' : 'up';

    return `${Math.round(xTranslation)}(${xDirection}) ${Math.round(yTranslation)}(${yDirection})`;
};

export const setFullMediaTranslation = (
    asset: TimeAwareMediaAsset,
    pixelsInX: number,
    pixelsInY: number,
) => {
    const { width, height } = getMediaViewport(asset);
    const xLimit = (asset.width - width) / 2.0;
    const yLimit = (asset.height - height) / 2.0;

    // Valid translation range is always [-abs(limit), +abs(limit)]
    if (Math.abs(pixelsInX) > Math.abs(xLimit)) {
        throw new Error(
            `Requested x translation ${pixelsInX} pixels exceeds limit of ±${Math.abs(xLimit).toFixed(2)} pixels`,
        );
    }
    if (Math.abs(pixelsInY) > Math.abs(yLimit)) {
        throw new Error(
            `Requested y translation ${pixelsInY} pixels exceeds limit of ±${Math.abs(yLimit).toFixed(2)} pixels`,
        );
    }
    asset.xTranslation = pixelsInX / asset.width;
    asset.yTranslation = pixelsInY / asset.height;
};

export const getFullMediaScale = (asset: TimeAwareMediaAsset): string => {
    const scale = Math.round(asset.scaleX * 100); // scaleX and scaleY should always be the same
    return `${scale}%`;
};

export const getMediaBrightness = (asset: TimeAwareMediaAsset): number => {
    return Math.round(asset.brightness * 100) / 100;
};

/* Get the full media translation range in pixels. This corresponds to how much the media can move within the visible area with the current scale value. We can increase the range by increasing the scale. */
export const getFullMediaTranslationRange = (
    asset: TimeAwareMediaAsset,
): { mediaXTranslationRange: [number, number]; mediaYTranslationRange: [number, number] } => {
    const { width, height } = getMediaViewport(asset);
    const xRange = Math.round((asset.width - width) / 2.0);
    const yRange = Math.round((asset.height - height) / 2.0);

    const mediaXTranslationRange: [number, number] = [-xRange, xRange];
    const mediaYTranslationRange: [number, number] = [-yRange, yRange];

    return { mediaXTranslationRange, mediaYTranslationRange };
};
